import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import ZoneDashboard from "../ZoneDashboard";
import RsmDashboard from "../RsmDashboard";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Tata from "../../../Artifacts/tata-motors-white.svg";
import { useSelector } from "react-redux";

const ServiceColabaxTabs = forwardRef((props, ref) => {
  const [activeTab, setActiveTab] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const { data: filterData } = useSelector(
    (state) => state.filtersServiceCollabax,
  );

  const filterLabels = {
    region: "Region",
    state: "State",
    state_office: "State Office",
    Rsm_Name: "RSM",
    Partner_Type: "Partner Type",
    dealer_tag: "Category",
    dealer_name: "Dealer Name",
    dealer_code: "Dealer Code",
    division_name: "Division",
    Product_Line: "Product Line",
    lob: "LOB",
    ppl: "PPL",
    pl: "PL",
  };

  const activeFilters = Object.entries(filterData || {}).filter(
    ([key, value]) => value && value.length > 0,
  );

  const renderActiveFilters = () => {
    if (activeFilters.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-2 p-2 bg-white w-full rounded-b-md">
        {activeFilters.map(([key, value]) => (
          <div
            key={key}
            className="text-sm text-[#2A5775] flex bg-gray-50 border border-gray-200 px-2 py-1 rounded"
          >
            <span className="font-bold mr-1">{filterLabels[key] || key}: </span>
            <span className="font-normal">{value.join(", ")}</span>
          </div>
        ))}
      </div>
    );
  };

  const zoneRef = useRef(null);
  const rsmRef = useRef(null);

  useImperativeHandle(ref, () => ({
    handleExportPDF: async () => {
      if (isExporting) return;
      setIsExporting(true);

      const targetRef = activeTab === 1 ? zoneRef : rsmRef;
      const dashboardTitle =
        activeTab === 1 ? "Zone Dashboard" : "RSM Dashboard";

      if (!targetRef.current) {
        setIsExporting(false);
        return;
      }

      try {
        const element = targetRef.current;
        const pdfHeader = element.querySelector("[data-pdf-header]");
        const kpiCards = element.querySelector("[data-pdf-cards]");
        const accordions = element.querySelectorAll(
          "[data-pdf-accordion-wrapper]",
        );

        // Temporarily force all accordions to be visible and expanded
        const accordionContents = element.querySelectorAll(
          "[data-accordion-content]",
        );
        const originalStyles = [];

        accordionContents.forEach((div) => {
          originalStyles.push({
            el: div,
            height: div.style.height,
            opacity: div.style.opacity,
            visibility: div.style.visibility,
            overflow: div.style.overflow,
          });
          div.style.height = "auto";
          div.style.opacity = "1";
          div.style.visibility = "visible";
          div.style.overflow = "visible";
        });

        // Show PDF header temporarily for capture
        if (pdfHeader) pdfHeader.style.display = "flex";

        // Trigger resize event to force Echarts charts to re-render
        window.dispatchEvent(new Event("resize"));
        window.dispatchEvent(new Event("pdf-export-start"));
        await new Promise((resolve) => setTimeout(resolve, 3000)); // Increased slightly to allow API calls to finish

        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const contentWidth = pdfWidth - 2 * margin;

        let currentY = 0;
        let isFirstPage = true;

        const captureAndAddHeader = async () => {
          if (!pdfHeader) return 0;
          const canvas = await html2canvas(pdfHeader, {
            scale: 2,
            useCORS: true,
          });
          const imgData = canvas.toDataURL("image/png");
          const imgHeight = (canvas.height * contentWidth) / canvas.width;
          pdf.addImage(imgData, "PNG", margin, 5, contentWidth, imgHeight);
          return imgHeight + 10; // Return height plus some spacing
        };

        const captureAndAddElement = async (el, yOffset) => {
          if (!el) return 0;
          const canvas = await html2canvas(el, { scale: 2, useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const imgHeight = (canvas.height * contentWidth) / canvas.width;

          // Check if it fits on current page
          if (yOffset + imgHeight > pdfHeight - margin) {
            pdf.addPage();
            yOffset = await captureAndAddHeader();
          }

          pdf.addImage(
            imgData,
            "PNG",
            margin,
            yOffset,
            contentWidth,
            imgHeight,
          );
          return imgHeight;
        };

        // --- Start Generation ---

        // Page 1: Header + Cards
        currentY = await captureAndAddHeader();
        const cardsHeight = await captureAndAddElement(kpiCards, currentY);
        currentY += cardsHeight + 5;

        // Process Accordions
        for (let i = 0; i < accordions.length; i++) {
          const acc = accordions[i];
          const accHeader = acc.querySelector("[data-pdf-accordion-header]");
          const charts = acc.querySelectorAll("[data-pdf-chart]");

          // Except for first accordion, start a new page
          if (i > 0) {
            pdf.addPage();
            currentY = await captureAndAddHeader();
          }

          // Add Accordion Header
          const accHeaderHeight = await captureAndAddElement(
            accHeader,
            currentY,
          );
          currentY += accHeaderHeight + 2;

          // Process Charts in groups (rows) and enforce 15 charts per page
          // Step is 3 for Zone (3 charts per row), 1 for RSM (1 full-width row)
          const step = activeTab === 1 ? 3 : 1;
          const chartsOnCurrentPageLimit = 15;
          let chartsOnCurrentPage = 0;
          const chartsArray = Array.from(charts);

          if (chartsArray.length === 0) {
            // Fallback: If no charts are tagged, capture the entire accordion content area
            const accordionContent = acc.querySelector(
              "[data-accordion-content]",
            );
            if (accordionContent) {
              await captureAndAddElement(accordionContent, currentY);
            }
          } else {
            for (let j = 0; j < chartsArray.length; j += step) {
              // Check if adding this row exceeds 15 charts on the current page
              if (chartsOnCurrentPage >= chartsOnCurrentPageLimit) {
                pdf.addPage();
                currentY = await captureAndAddHeader();
                const repeatedHeaderHeight = await captureAndAddElement(
                  accHeader,
                  currentY,
                );
                currentY += repeatedHeaderHeight + 2;
                chartsOnCurrentPage = 0;
              }

              // Capture the row (step charts)
              const rowCharts = chartsArray.slice(j, j + step);
              if (rowCharts.length === 0) continue;

              const firstChartRect = rowCharts[0].getBoundingClientRect();
              const dashboardRect = element.getBoundingClientRect();

              const x = 0; // Use full horizontal strip
              const y = firstChartRect.top - dashboardRect.top;
              const width = element.clientWidth;
              const height =
                Math.max(...rowCharts.map((c) => c.offsetHeight)) + 4; // Add slight padding

              const rowCanvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                x: x,
                y: y,
                width: width,
                height: height,
                scrollX: 0,
                scrollY: 0,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
              });

              const rowImgData = rowCanvas.toDataURL("image/png");
              const rowImgHeight =
                (rowCanvas.height * contentWidth) / rowCanvas.width;

              // Handle page break if row doesn't fit
              if (currentY + rowImgHeight > pdfHeight - margin) {
                pdf.addPage();
                currentY = await captureAndAddHeader();
                const repeatedHeaderHeight = await captureAndAddElement(
                  accHeader,
                  currentY,
                );
                currentY += repeatedHeaderHeight + 2;
                chartsOnCurrentPage = 0; // Reset count since it's a new page
              }

              pdf.addImage(
                rowImgData,
                "PNG",
                margin,
                currentY,
                contentWidth,
                rowImgHeight,
              );
              currentY += rowImgHeight + 2;

              // In RSM, one row might contain multiple "business" graphs
              // But for simplicity of pagination we count the tagged entries
              chartsOnCurrentPage += rowCharts.length;
            }
          }
        }

        // Restore everything
        originalStyles.forEach((s) => {
          s.el.style.height = s.height;
          s.el.style.opacity = s.opacity;
          s.el.style.visibility = s.visibility;
          s.el.style.overflow = s.overflow;
        });

        if (pdfHeader) pdfHeader.style.display = "none";
        window.dispatchEvent(new Event("resize"));

        pdf.save(`${dashboardTitle.replace(/\s+/g, "_")}_Export.pdf`);
      } catch (error) {
        console.error("PDF Export failed", error);
      } finally {
        setIsExporting(false);
      }
    },
  }));

  return (
    <div className="p-2 pt-0 w-full max-w-[100vw] overflow-x-hidden">
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2a5775]"></div>
            <p className="font-semibold text-[#2a5775]">Generating PDF...</p>
            <p className="text-sm text-gray-500">
              Capturing all graphs, please wait.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          className={`px-4 py-2 rounded-full text-sm border border-gray-300 shadow-md transition ${
            activeTab === 1
              ? "bg-[#1E293B] text-white font-bold shadow-lg"
              : "bg-white text-black hover:shadow-lg"
          }`}
          onClick={() => setActiveTab(1)}
        >
          Zone Dashboard
        </button>

        <button
          className={`px-4 py-2 rounded-full text-sm border border-gray-300 shadow-md transition ${
            activeTab === 2
              ? "bg-[#1E293B] text-white font-bold shadow-lg"
              : "bg-white text-black hover:shadow-lg"
          }`}
          onClick={() => setActiveTab(2)}
        >
          RSM All Dashboard
        </button>
      </div>
      <div className="mt-2">
        <div
          ref={zoneRef}
          style={{ display: activeTab === 1 ? "block" : "none" }}
        >
          <div
            data-pdf-header
            style={{ display: "none" }}
            className="flex flex-col w-full my-2 bg-white rounded-md border border-[#2A5775]"
          >
            <div className="bg-[#2A5775] text-xl flex items-center justify-between px-4 text-center w-full text-white font-bold p-2">
              <img
                src={Tata}
                alt="Tata Motors"
                loading="eager"
                className="h-[42px] w-auto"
              />
              <span>Snapshot for Zone Dashboard</span>
              <div className="w-[42px]"></div>
            </div>
            {renderActiveFilters()}
          </div>
          <ZoneDashboard />
        </div>
        <div
          ref={rsmRef}
          style={{ display: activeTab === 2 ? "block" : "none" }}
        >
          <div
            data-pdf-header
            style={{ display: "none" }}
            className="flex flex-col w-full my-2 bg-white rounded-md border border-[#2A5775]"
          >
            <div className="bg-[#2A5775] text-xl flex items-center justify-between px-4 text-center w-full text-white font-bold p-2">
              <img
                src={Tata}
                alt="Tata Motors"
                loading="eager"
                className="h-[42px] w-auto"
              />
              <span>Snapshot for RSM Dashboard</span>
              <div className="w-[42px]"></div>
            </div>
            {renderActiveFilters()}
          </div>
          <RsmDashboard />
        </div>
      </div>
    </div>
  );
});

ServiceColabaxTabs.displayName = "ServiceColabaxTabs";
export default ServiceColabaxTabs;
